import React, { useRef, useEffect } from 'react';
import { LobbyMessage, Profile } from '@/types/supabase';
import { UserConnection } from '@/hooks/useUserMessages';
import LobbyMessageItem from './LobbyMessageItem';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';

interface MessageListProps {
  messages: LobbyMessage[];
  loading: boolean;
  onLoadMore: () => void;
  hasMore: boolean;
  loadingMore: boolean;
  onToggleReaction: (messageId: string, emoji: any) => void;
  onDelete: (messageId: string) => Promise<void>;
  onEdit: (messageId: string, content: string) => Promise<void>;
  profile: Profile | null;
  connections: UserConnection[];
  onConnect: (userId: string, userName: string) => void;
  channelMembers: Profile[];
}

const MessageList: React.FC<MessageListProps> = ({
  messages,
  loading,
  onLoadMore,
  hasMore,
  loadingMore,
  onToggleReaction,
  onDelete,
  onEdit,
  profile,
  connections,
  onConnect,
  channelMembers,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const prevMessagesLengthRef = useRef(messages.length);
  const isInitialChannelLoad = useRef(true);

  useEffect(() => {
    if (loading) {
      isInitialChannelLoad.current = true;
    }
  }, [loading]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const scrollToBottom = () => {
      setTimeout(() => {
        if (scrollContainer) {
          scrollContainer.scrollTop = scrollContainer.scrollHeight;
        }
      }, 0);
    };

    if (isInitialChannelLoad.current && !loading && messages.length > 0) {
      // First load for a channel, always scroll to bottom
      scrollToBottom();
      isInitialChannelLoad.current = false;
    } else if (messages.length > prevMessagesLengthRef.current) {
      // New message received, check if user is near the bottom
      const isScrolledToBottom =
        scrollContainer.scrollHeight - scrollContainer.scrollTop <=
        scrollContainer.clientHeight + 150; // Threshold

      if (isScrolledToBottom) {
        scrollToBottom();
      }
    }

    prevMessagesLengthRef.current = messages.length;
  }, [messages, loading]);

  if (loading) {
    return (
      <div className="p-4 space-y-4">
        {[...Array(10)].map((_, i) => (
          <div key={i} className="flex items-start gap-4">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-full" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div ref={scrollContainerRef} className="h-full overflow-y-auto p-4 space-y-4">
      {hasMore && (
        <div className="text-center">
          <Button onClick={onLoadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading...' : 'Load More Messages'}
          </Button>
        </div>
      )}
      {messages.map((message) => (
        <LobbyMessageItem
          key={message.id}
          message={message}
          profile={profile}
          onEdit={onEdit}
          onDelete={onDelete}
          onToggleReaction={onToggleReaction}
          connections={connections}
          onConnect={onConnect}
          channelMembers={channelMembers}
        />
      ))}
    </div>
  );
};

export default MessageList;